import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Backend `ResetPasswordDto` / `SetupPasswordDto` regex — kept in sync
 * with `normax-trust-banking-backend/src/modules/auth/dto/reset-password.dto.ts`.
 * Requires at least one lowercase, one uppercase, one digit, one of the
 * listed symbols, and minimum length 8.
 */
const PASSWORD_STRENGTH_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;

/**
 * Validator that enforces the backend password-strength regex. Pairs with
 * `Validators.minLength(8)` and `Validators.maxLength(128)` for full
 * coverage of the backend DTO constraints.
 */
export const passwordStrengthValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const value = control.value;
  if (typeof value !== 'string' || value.length === 0) {
    // Empty handled by Validators.required.
    return null;
  }
  return PASSWORD_STRENGTH_REGEX.test(value) ? null : { passwordStrength: true };
};

/**
 * Cross-field validator factory — confirms two named controls match.
 * Attach via `{ validators: passwordMatchValidator('newPassword', 'confirmPassword') }`
 * on the parent FormGroup.
 */
export function passwordMatchValidator(
  passwordKey: string,
  confirmKey: string,
): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const password = group.get(passwordKey)?.value;
    const confirm = group.get(confirmKey)?.value;
    if (!password || !confirm) return null;
    return password === confirm ? null : { passwordMismatch: true };
  };
}
